import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';

import {
  ClearDateButton,
  HoverableDayCell,
} from '@components/DatePicker/HoverableDayCell';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import { FilterSheetFooter } from '@components/shared/FilterSheet/FilterSheetFooter';
import { FilterSheetHeader } from '@components/shared/FilterSheet/FilterSheetHeader';
import Icon from '@components/shared/Icon/Icon';
import { useHover } from '@hooks/useHover';
import theme from '@themes/theme';
import { beginningOfToday } from '@utils/formatters/date';

type DatePickerProps = {
  minDate?: Date;
  maxDate?: Date;
  selectedDate: Date;
  onSelectDate: (date: Date | undefined) => void;
  isBottomSheet?: boolean;
  onClose?: () => void;
  title?: string;
  isSubmitting?: boolean;
  /**
   * Skips the root box's flex stretch so it sizes to its own content instead
   * of filling whatever height its container hands it. Needed by callers
   * (TaskCreateBottomSheet's Due Date modal) that size the sheet itself to an
   * exact, pre-measured content height — see `computeDateModalContentHeight`
   * in `taskCreateBottomSheetSnapPoints.ts`. The stretch stays the default:
   * other `isBottomSheet` callers (e.g. `TaskEditDateModal`) size their sheet
   * as a ratio of the screen and rely on this box growing to fill it.
   */
  shouldHugContent?: boolean;
};

export const MONTH_NAME = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const GetDays = (year: number, month: number) => {
  const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) {
    days[1] = 29;
  }
  return days[month];
};

type Cell = { y: number; m: number; d: number; inMonth: boolean };

type DayCellProps = {
  cell: Cell;
  disabled: boolean;
  today: boolean;
  selectedDay: boolean;
  textColor?: object;
  onPress: () => void;
};

// TA-10950 #7/#26: grey01 hover on every selectable date, disabled cursor on
// dates outside min/maxDate.
const DayCell: React.FC<DayCellProps> = ({
  cell,
  disabled,
  today,
  selectedDay,
  textColor,
  onPress,
}) => (
  <HoverableDayCell
    hoverDisabled={disabled || selectedDay}
    touchDisabled={disabled}
    onPress={onPress}
    cursor={disabled ? 'not-allowed' : 'pointer'}
    accessibilityLabel={`Calendar | ${cell.d}`}
    radius={CELL_SIZE / 2}
    width={CELL_SIZE}
    height={CELL_SIZE}>
    <Box alignItems='center' justifyContent='center' style={styles.cellWrap}>
      <Box
        style={[
          styles.circle,
          today && styles.todayCircle,
          selectedDay && styles.selectedCircle,
        ]}>
        <Text
          variant='webBodySecondary'
          allowFontScaling={false}
          style={[styles.dayText, textColor]}>
          {cell.d}
        </Text>
      </Box>
    </Box>
  </HoverableDayCell>
);

const DatePicker: React.FC<DatePickerProps> = ({
  onSelectDate,
  selectedDate,
  minDate,
  maxDate,
  isBottomSheet,
  onClose,
  title,
  isSubmitting = false,
  shouldHugContent = false,
}) => {
  const { t } = useTranslation('format');

  const initDate = selectedDate || beginningOfToday();

  const [viewYear, setViewYear] = useState(initDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initDate.getMonth());

  const [selected, setSelected] = useState<Date | undefined>(selectedDate);

  // TA-10950 #26: grey01 hover on the month-nav arrows. Hover state lives on
  // the same full-size Box that's already the TouchableOpacity's only child
  // (not delegated to WebIcon) so the native tap target stays the full
  // MONTH_NAV_BUTTON_SIZE box instead of shrinking to the icon's own bounds.
  const { isHovered: isMonthPreHovered, hoverProps: monthPreHoverProps } =
    useHover();
  const { isHovered: isMonthNextHovered, hoverProps: monthNextHoverProps } =
    useHover();

  useEffect(() => {
    setSelected(selectedDate);
    if (selectedDate) {
      setViewYear(selectedDate.getFullYear());
      setViewMonth(selectedDate.getMonth());
    }
  }, [selectedDate]);

  const monthPre = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((v) => v - 1);
    } else setViewMonth((v) => v - 1);
  };

  const monthNext = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((v) => v + 1);
    } else setViewMonth((v) => v + 1);
  };

  const cells: Cell[] = useMemo(() => {
    const daysCurrent = GetDays(viewYear, viewMonth);

    const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear;
    const prevMonth = viewMonth === 0 ? 11 : viewMonth - 1;
    const daysPrev = GetDays(prevYear, prevMonth);

    const firstDayWeek = new Date(viewYear, viewMonth, 1).getDay();

    const list: Cell[] = [];

    for (let i = 0; i < firstDayWeek; i++) {
      const d = daysPrev - firstDayWeek + 1 + i;
      list.push({ y: prevYear, m: prevMonth, d, inMonth: false });
    }

    for (let d = 1; d <= daysCurrent; d++) {
      list.push({ y: viewYear, m: viewMonth, d, inMonth: true });
    }

    const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear;
    const nextMonth = viewMonth === 11 ? 0 : viewMonth + 1;

    let d = 1;
    while (list.length < 42) {
      list.push({ y: nextYear, m: nextMonth, d, inMonth: false });
      d++;
    }

    return list;
  }, [viewYear, viewMonth]);

  const isDisabled = (date: Date) =>
    (!!minDate && date < minDate) || (!!maxDate && date > maxDate);

  const isSameDay = (a?: Date, b?: Date) =>
    !!a &&
    !!b &&
    dayjs(a).format('YYYY-MM-DD') === dayjs(b).format('YYYY-MM-DD');

  const onPressCell = (cell: Cell) => {
    const date = new Date(cell.y, cell.m, cell.d);
    if (isDisabled(date)) return;

    if (isSameDay(selected, date)) {
      setSelected(undefined);
      return;
    }

    setSelected(date);
  };

  let rootFlex: number | undefined;
  if (!shouldHugContent) {
    rootFlex = Platform.OS === 'web' ? 1 : 0.9;
  }

  // A task with no existing due date and no newly picked one has nothing to
  // submit — Confirm/Done must no-op instead of calling onSelectDate(undefined),
  // which would otherwise enter the submit path for a null change.
  const canConfirm = !!selected || !!selectedDate;

  const doneSelectDate = () => {
    if (!canConfirm) return;
    onSelectDate(selected);
  };

  const renderCell = (cell: Cell, idx: number) => {
    const date = new Date(cell.y, cell.m, cell.d);

    const filler = !cell.inMonth;
    const disabled = isDisabled(date);
    const today = isSameDay(date, new Date());
    const selectedDay = isSameDay(selected, date);
    const getTextColor = () => {
      if (selectedDay) return styles.daySelectText;
      if (disabled) return styles.dayNotSelect;
      if (filler) return styles.fillerDay;
      return undefined;
    };

    const textColor = getTextColor();

    return (
      <DayCell
        key={`${cell.y}-${cell.m}-${cell.d}-${idx}`}
        cell={cell}
        disabled={disabled}
        today={today}
        selectedDay={selectedDay}
        textColor={textColor}
        onPress={() => onPressCell(cell)}
      />
    );
  };

  return (
    <Box
      paddingVertical={isBottomSheet ? undefined : 'm'}
      paddingHorizontal='m'
      flex={rootFlex}>
      {isBottomSheet ? (
        // TA-10495 #3: X-close icon + no header Done button — the Done/Cancel
        // FloatingHeader this used to render was the outdated bottom-sheet
        // pattern; Reset/Apply below now own committing the selection.
        <FilterSheetHeader
          title={title ?? t('models:tasks.create.dates.modalTitle')}
          icon='X'
          onPress={() => onClose?.()}
          testID='due-date-sheet-close'
          accessibilityLabel={t('shared:close')}
        />
      ) : (
        <Box
          flexDirection='row'
          justifyContent={Platform.OS === 'web' ? 'center' : 'flex-end'}
          alignItems='center'>
          <Text variant='labelLarge'>{title ?? t('shared:calendar')}</Text>
          {Platform.OS !== 'web' && (
            <Box alignItems='flex-end' width={96}>
              <TouchableOpacity onPress={doneSelectDate} disabled={!canConfirm}>
                <Text
                  color={canConfirm ? 'greenSecondary' : 'grey04'}
                  variant='buttonLabel'>
                  Done
                </Text>
              </TouchableOpacity>
            </Box>
          )}
        </Box>
      )}
      {/*
        backgroundColor='white' is required here — without it, Android
        collapses this pure-layout Box (view flattening) since it has no
        paint properties, which can make RNGH mis-route taps meant for the
        month-nav buttons to the bottom sheet's own dismiss gesture. Do not
        remove.
      */}
      <Box
        flexDirection='row'
        alignItems='center'
        backgroundColor='white'
        marginTop={isBottomSheet ? 'xs' : 'm'}
        marginBottom='s'
        height={MONTH_NAV_BUTTON_SIZE}>
        <TouchableOpacity
          onPress={monthPre}
          accessibilityLabel='Previous month'>
          <Box
            {...monthPreHoverProps}
            height={MONTH_NAV_BUTTON_SIZE}
            width={MONTH_NAV_BUTTON_SIZE}
            borderRadius='xs'
            justifyContent='center'
            alignItems='center'
            backgroundColor={isMonthPreHovered ? 'grey01' : undefined}>
            <Icon name='ChevronLeft' variant='l' color='textPrimary' />
          </Box>
        </TouchableOpacity>
        <Box alignItems='center' flex={1}>
          <Text textAlign='center' variant='bodyTaskName'>
            {MONTH_NAME[viewMonth]} {viewYear}
          </Text>
        </Box>
        <TouchableOpacity onPress={monthNext} accessibilityLabel='Next month'>
          <Box
            {...monthNextHoverProps}
            height={MONTH_NAV_BUTTON_SIZE}
            width={MONTH_NAV_BUTTON_SIZE}
            borderRadius='xs'
            justifyContent='center'
            alignItems='center'
            backgroundColor={isMonthNextHovered ? 'grey01' : undefined}>
            <Icon name='ChevronRight' variant='l' color='textPrimary' />
          </Box>
        </TouchableOpacity>
      </Box>
      <Box
        flexDirection='row'
        style={styles.dayContainer}
        justifyContent='space-around'
        paddingBottom='xs'>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((x, index) => (
          <Text
            key={x + index.toString()}
            variant='metadata'
            textAlign='center'>
            {x}
          </Text>
        ))}
      </Box>
      {Array.from({ length: 6 }).map((_, row) => (
        <Box
          key={`row-${cells[row * 7]?.y}-${cells[row * 7]?.m}-${
            cells[row * 7]?.d
          }`}
          py='xxs'
          backgroundColor='white'
          flexDirection='row'
          justifyContent='space-between'>
          {cells
            .slice(row * 7, row * 7 + 7)
            .map((cell, idx) => renderCell(cell, idx))}
        </Box>
      ))}
      {isBottomSheet ? (
        // TA-10495 #3/#6: Reset/Apply footer matching every other filter
        // sheet, with Apply's loading state showing only a spinner (no
        // "Submitting" text) while the mutation is in flight.
        <FilterSheetFooter
          testIDPrefix='due-date-sheet'
          resetEnabled={!!selected}
          applyEnabled={canConfirm}
          applying={isSubmitting}
          // Reset clears the draft selection rather than restoring the task's
          // existing due date — otherwise Reset is a no-op whenever the sheet
          // opened on an already-dated task, and Reset→Apply could never
          // clear the date. `canConfirm` still holds via `selectedDate`, so
          // Apply submits `undefined` and the due date is removed.
          onReset={() => setSelected(undefined)}
          onApply={doneSelectDate}
        />
      ) : (
        <Box
          borderTopWidth={1}
          borderTopColor='grey02'
          flexDirection='row'
          alignItems='center'
          pt='s'
          height={72}
          px='m'>
          <Box flex={1}>
            <ClearDateButton
              hoverDisabled={!selected}
              touchDisabled={!selected}
              onPress={() => setSelected(undefined)}
              cursor={selected ? 'pointer' : 'not-allowed'}
              width={125}
              height={48}
              textColor={!selected ? 'grey04' : 'textPrimary'}
            />
          </Box>

          <Box flex={1} marginLeft='m'>
            <Button
              disabled={isSubmitting || !canConfirm}
              fullWidth
              isSmall
              height={48}
              onPress={() => {
                doneSelectDate();
              }}
              backgroundColor={isSubmitting || !canConfirm ? 'grey02' : 'black'}
              borderRadius='xl'
              variant='primary'
              accessibilityLabel={t('shared:confirm')}>
              <Box flexDirection='row' alignItems='center'>
                {isSubmitting && (
                  <ActivityIndicator
                    style={{ height: 12 }}
                    color={theme.colors.grey04}
                  />
                )}
                <Text
                  marginLeft={isSubmitting ? 'xs' : 'none'}
                  variant='labelSmall'
                  color={isSubmitting || !canConfirm ? 'grey04' : 'white'}>
                  {isSubmitting ? 'Submitting' : t('shared:confirm')}
                </Text>
              </Box>
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const CELL_SIZE = 32;
const MONTH_NAV_BUTTON_SIZE = theme.spacing.xl + theme.spacing.xxs;

const styles = StyleSheet.create({
  dayContainer: {
    shadowColor: 'black',
    marginHorizontal: Platform.OS === 'ios' ? -4 : -2,
    shadowOpacity: 0.06,
    shadowOffset: { width: 0, height: 12 },
    shadowRadius: 12,
    backgroundColor: 'white',
  },

  cellWrap: {
    width: CELL_SIZE,
    height: CELL_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },

  circle: {
    width: CELL_SIZE,
    height: CELL_SIZE,
    borderRadius: CELL_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },

  selectedCircle: {
    backgroundColor: theme.colors.black,
  },

  todayCircle: {
    borderWidth: 1,
    borderColor: theme.colors.grey05,
  },

  dayText: {
    width: CELL_SIZE,
    height: CELL_SIZE,
    textAlign: 'center',
    fontSize: 12,
    includeFontPadding: false,
    textAlignVertical: 'center', // Android vertical center
    lineHeight: CELL_SIZE, // iOS + Android stable vertical center
  },

  daySelectText: { color: theme.colors.white },
  dayNotSelect: { color: theme.colors.grey04 },
  fillerDay: { color: theme.colors.grey04 },
});

export default DatePicker;
